import { formatDateTime } from './datetime';

export interface ExcelColumn<T> {
  header: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  numFmt?: string;
  map: (row: T, index: number) => string | number | Date | null | undefined;
}

export interface ExcelExportOptions<T> {
  fileName: string;
  sheetName?: string;
  title: string;
  subtitle?: string | undefined;
  columns: ExcelColumn<T>[];
  rows: T[];
}

const BRAND = {
  headerFill: 'FF1F4E4A',
  headerText: 'FFFFFFFF',
  titleText: 'FF16332F',
  subtitleText: 'FF5B6B69',
  zebraFill: 'FFF0F7F5',
  borderColor: 'FFDCE7E4',
  footerText: 'FF7A8A87',
} as const;

export const exportRowsToExcel = async <T,>(options: ExcelExportOptions<T>): Promise<void> => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Hospital Management System';
  workbook.created = new Date();

  const columnCount = options.columns.length;
  const sheet = workbook.addWorksheet(options.sheetName ?? 'Report', {
    views: [{ state: 'frozen', ySplit: 4, showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  sheet.mergeCells(1, 1, 1, columnCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = options.title;
  titleCell.font = { name: 'Calibri', size: 15, bold: true, color: { argb: BRAND.titleText } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  sheet.getRow(1).height = 24;

  sheet.mergeCells(2, 1, 2, columnCount);
  const subtitleCell = sheet.getCell(2, 1);
  const generatedAt = formatDateTime(new Date());
  subtitleCell.value = [options.subtitle, `Generated ${generatedAt}  ·  ${options.rows.length} record${options.rows.length === 1 ? '' : 's'}`]
    .filter(Boolean).join('   |   ');
  subtitleCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: BRAND.subtitleText } };
  sheet.getRow(2).height = 16;
  sheet.getRow(3).height = 5;

  const headerRowIndex = 4;
  const headerRow = sheet.getRow(headerRowIndex);
  options.columns.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = column.header;
    cell.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: BRAND.headerText } };
    cell.alignment = { horizontal: column.align ?? 'left', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND.headerFill } };
    cell.border = { top: { style: 'thin', color: { argb: BRAND.headerFill } }, bottom: { style: 'medium', color: { argb: BRAND.headerFill } } };
  });
  headerRow.height = 20;

  options.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(headerRowIndex + 1 + rowIndex);
    options.columns.forEach((column, columnIndex) => {
      const cell = excelRow.getCell(columnIndex + 1);
      const value = column.map(row, rowIndex);
      cell.value = value === null || value === undefined || value === '' ? '-' : value;
      if (column.numFmt && typeof value === 'number') cell.numFmt = column.numFmt;
      cell.font = { name: 'Calibri', size: 10.5, color: { argb: BRAND.titleText } };
      cell.alignment = { horizontal: column.align ?? 'left', vertical: 'middle' };
      cell.border = { bottom: { style: 'hair', color: { argb: BRAND.borderColor } } };
      if (rowIndex % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND.zebraFill } };
    });
    excelRow.height = 17;
  });

  options.columns.forEach((column, index) => {
    sheet.getColumn(index + 1).width = column.width ?? Math.max(column.header.length + 4, 13);
  });

  if (options.rows.length > 0) {
    sheet.autoFilter = { from: { row: headerRowIndex, column: 1 }, to: { row: headerRowIndex, column: columnCount } };
  }

  const footerRowIndex = headerRowIndex + options.rows.length + 2;
  sheet.mergeCells(footerRowIndex, 1, footerRowIndex, columnCount);
  const footerCell = sheet.getCell(footerRowIndex, 1);
  footerCell.value = 'Generated from Hospital Management System — for internal use only.';
  footerCell.font = { name: 'Calibri', size: 8.5, italic: true, color: { argb: BRAND.footerText } };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.fileName.toLowerCase().endsWith('.xlsx') ? options.fileName : `${options.fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

interface PagedResult<T> { data: T[]; pagination: { totalPages: number } }

export const fetchAllPages = async <T,>(
  fetchPage: (page: number, limit: number) => Promise<PagedResult<T>>,
  pageSize = 500,
): Promise<T[]> => {
  const first = await fetchPage(1, pageSize);
  const rows = [...first.data];
  for (let page = 2; page <= first.pagination.totalPages; page += 1) {
    const next = await fetchPage(page, pageSize);
    rows.push(...next.data);
  }
  return rows;
};
